/**
 * SB6-ANA-CACHE-02 — wire the prefetch scheduler to the live analysis stores.
 *
 * `usePrefetch` is mounted once by `AnalysisFeature`. It:
 * - binds the scheduler to the active acquisition + total image count;
 * - installs the SAS re-mint hook (a `refetch()` of the image list → fresh SAS
 *   batch → `imageId → fullUrl` map the scheduler swaps in);
 * - on every activation OR filter-order change, recomputes the P0 active image
 *   + the P1 window (next `WINDOW_AHEAD` / prev `WINDOW_BEHIND` in the **current
 *   filtered order**) and hands it to the scheduler, which re-prioritises
 *   instantly;
 * - PERF-06 — warms the per-image OVERLAY queries (findings + defects) for
 *   the next few window images via `useOverlayPrefetch`, with keys built by
 *   the same builders the mounted hooks use (cache hits, not duplicates);
 * - exposes a `downloadDataset()` / `cancelDataset()` pair for the explicit
 *   "Download dataset" action (P2), enqueuing the filtered order first then the
 *   rest of the acquisition.
 *
 * Window prefetch is **automatic**; whole-dataset warm is **explicit only**
 * (Q1). `usePrefetchProgress` is the React read-surface over the scheduler's
 * external store (statusbar in PROG-01; interim inline affordance here).
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import type { ImageRef } from "@aeriosiq/types";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useFilteredDataPoints } from "../useFilteredDataPoints.js";
import { useImagesQuery } from "../queries/useImagesQuery.js";
import {
  prefetchScheduler,
  WINDOW_AHEAD,
  WINDOW_BEHIND,
  type PrefetchImage,
  type PrefetchProgress,
} from "./prefetchQueue.js";
import { cacheManifest } from "./cacheManifest.js";
import { setOpenAcquisitionId } from "./manifestBridge.js";
import { useOverlayPrefetch } from "./useOverlayPrefetch.js";

export interface UsePrefetchResult {
  /** Start the explicit whole-dataset (P2) warm. */
  downloadDataset: () => void;
  /** Cancel an in-progress dataset download (drops pending P2 entries). */
  cancelDataset: () => void;
  /** Live progress snapshot (cachedCount / totalCount / isDownloading / error). */
  progress: PrefetchProgress;
  /**
   * `navigator.connection.saveData` (or equivalent metered hint), if exposed.
   * The UI warns — but does not block — before a dataset download on a metered
   * connection (decided 2026-06-05).
   */
  saveDataHint: boolean;
}

/** Read `navigator.connection.saveData` defensively (not in all browsers). */
function readSaveDataHint(): boolean {
  if (typeof navigator === "undefined") return false;
  const conn = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  return Boolean(conn?.saveData);
}

/**
 * Build the filtered-order list of images that have a full-res URL. The order
 * is `useFilteredDataPoints().dataPoints` (the order the user navigates),
 * mapped to the image list's `urls.full` by `visImageId`. Images present in the
 * data-point set but missing from the image list (or without a full URL) are
 * dropped.
 */
function useFilteredImageOrder(dataAcquisitionId: string): {
  ordered: PrefetchImage[];
  byId: Map<string, ImageRef>;
  /** PERF-06 — the filtered set's sync flag, gating the overlay prefetch. */
  outOfSync: boolean;
} {
  const { dataPoints, outOfSync } = useFilteredDataPoints(dataAcquisitionId);
  const imagesQuery = useImagesQuery(dataAcquisitionId);

  const order = useMemo(() => {
    const byId = new Map<string, ImageRef>();
    for (const img of imagesQuery.data?.items ?? []) {
      byId.set(img.imageId, img);
    }
    const ordered: PrefetchImage[] = [];
    const seen = new Set<string>();
    for (const dp of dataPoints) {
      const imageId = dp.visImageId;
      if (!imageId || seen.has(imageId)) continue;
      const img = byId.get(imageId);
      if (!img?.urls.full) continue;
      seen.add(imageId);
      ordered.push({ imageId, fullUrl: img.urls.full });
    }
    return { ordered, byId };
  }, [dataPoints, imagesQuery.data?.items]);

  return { ordered: order.ordered, byId: order.byId, outOfSync };
}

export function usePrefetch(
  dataAcquisitionId: string,
  datasetName?: string,
): UsePrefetchResult {
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const { ordered, byId, outOfSync } = useFilteredImageOrder(dataAcquisitionId);
  const imagesQuery = useImagesQuery(dataAcquisitionId);

  // PERF-06 — warm the per-image overlay queries (findings + defects) for
  // the next few images of the same filtered window the image-byte
  // scheduler covers. Suspended while the filtered set is out of sync.
  useOverlayPrefetch({ dataAcquisitionId, ordered, activeImageId, outOfSync });

  // SB6-ANA-CACHE-03 — hydrate the cache manifest once (best-effort, IDB).
  useEffect(() => {
    void cacheManifest.hydrate();
  }, []);

  // SB6-ANA-CACHE-03 — mark this acquisition as the currently-open one so the
  // LRU evictor never removes it (AC7). Cleared on unmount.
  useEffect(() => {
    if (!dataAcquisitionId) return;
    setOpenAcquisitionId(dataAcquisitionId);
    return () => {
      setOpenAcquisitionId(null);
    };
  }, [dataAcquisitionId]);

  // Bind the scheduler to this acquisition + its image total. (Total is the
  // denominator of the progress readout; the filtered order may be a subset.)
  const totalImages = (imagesQuery.data?.items ?? []).length;
  useEffect(() => {
    if (!dataAcquisitionId) return;
    prefetchScheduler.setDataset(dataAcquisitionId, totalImages);
    // SB6-ANA-CACHE-03 — register the dataset's total + label into the manifest
    // so the storage UI and the Data Acquisitions list can show completeness
    // ("312/369") even before the first image is cached.
    cacheManifest.registerDataset(dataAcquisitionId, totalImages, datasetName);
  }, [dataAcquisitionId, totalImages, datasetName]);

  // Install the SAS re-mint hook: a list refetch re-mints all SAS URLs in one
  // call (Q7). We resolve to `imageId → fullUrl` so the scheduler can swap by
  // canonical key. Re-installed if `refetch` identity changes (rare).
  const refetchImages = imagesQuery.refetch;
  useEffect(() => {
    prefetchScheduler.setHostHooks({
      onSasExpired: async () => {
        const result = await refetchImages();
        const fresh = new Map<string, string>();
        for (const img of result.data?.items ?? []) {
          if (img.urls.full) fresh.set(img.imageId, img.urls.full);
        }
        return fresh;
      },
    });
    return () => {
      prefetchScheduler.setHostHooks(null);
    };
  }, [refetchImages]);

  // Re-prioritise on every activation OR filtered-order change: compute the P0
  // active image + the P1 window (ahead/behind) from the CURRENT filtered order
  // and hand both to the scheduler in one atomic update.
  useEffect(() => {
    if (!dataAcquisitionId || ordered.length === 0) return;

    const activeIdx = activeImageId
      ? ordered.findIndex((i) => i.imageId === activeImageId)
      : -1;

    // Active image: prefer the activated id (it may carry a full URL even if
    // it's filtered out of `ordered`); fall back to the first filtered image so
    // a freshly-opened dataset warms its window before the user clicks.
    let active: PrefetchImage | null = null;
    if (activeImageId) {
      const inOrder = ordered.find((i) => i.imageId === activeImageId);
      if (inOrder) {
        active = inOrder;
      } else {
        const img = byId.get(activeImageId);
        if (img?.urls.full) {
          active = { imageId: activeImageId, fullUrl: img.urls.full };
        }
      }
    }
    active ??= ordered[0] ?? null;

    // Window around the active index in filtered order.
    const center = activeIdx >= 0 ? activeIdx : 0;
    const window: PrefetchImage[] = [];
    for (let d = 1; d <= WINDOW_AHEAD; d += 1) {
      const next = ordered[center + d];
      if (next) window.push(next);
    }
    for (let d = 1; d <= WINDOW_BEHIND; d += 1) {
      const prev = ordered[center - d];
      if (prev) window.push(prev);
    }

    prefetchScheduler.setActiveAndWindow(active, window);
  }, [dataAcquisitionId, activeImageId, ordered, byId]);

  // Explicit "Download dataset": filtered order first, then any remaining
  // images of the acquisition (so a filtered-out image still gets cached, just
  // later).
  const downloadDataset = useCallback(() => {
    const filteredIds = new Set(ordered.map((i) => i.imageId));
    const rest: PrefetchImage[] = [];
    for (const img of imagesQuery.data?.items ?? []) {
      if (filteredIds.has(img.imageId)) continue;
      if (!img.urls.full) continue;
      rest.push({ imageId: img.imageId, fullUrl: img.urls.full });
    }
    prefetchScheduler.enqueueDataset([...ordered, ...rest]);
  }, [ordered, imagesQuery.data?.items]);

  const cancelDataset = useCallback(() => {
    prefetchScheduler.cancelDataset();
  }, []);

  const progress = usePrefetchProgress();
  const saveDataHint = useMemo(() => readSaveDataHint(), []);

  return { downloadDataset, cancelDataset, progress, saveDataHint };
}

/**
 * React read-surface over the scheduler's external progress store. Used by the
 * Download action's inline affordance now and by the PROG-01 statusbar later.
 */
export function usePrefetchProgress(): PrefetchProgress {
  return useSyncExternalStore(
    prefetchScheduler.subscribe,
    prefetchScheduler.getProgress,
    prefetchScheduler.getProgress,
  );
}
